import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import AdminNav from '../../components/AdminNav';
import { CheckCircle, XCircle, Loader2, User, Briefcase, MapPin, Clock } from 'lucide-react';

export default function PendingStaff() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const res = await adminAPI.getPendingStaff(); setStaff(res.data.data.content || []); }
    catch { toast.error('Failed to load pending staff'); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    setActionLoading(id);
    try { await adminAPI.approveRegistration(id); toast.success('Approved!'); setStaff(staff.filter(s => s.id !== id)); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed to approve'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Provide a rejection reason'); return; }
    setActionLoading(rejectId);
    try {
      await adminAPI.rejectRegistration(rejectId, rejectReason);
      toast.success('Rejected');
      setStaff(staff.filter(s => s.id !== rejectId));
      setRejectId(null); setRejectReason('');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to reject'); }
    finally { setActionLoading(null); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="h-10 w-10 animate-spin text-teal-600" /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav title="Pending Registrations" subtitle={staff.length + " awaiting approval"} />
      <div className="max-w-7xl mx-auto px-6 py-8">
        {staff.length === 0 ? (
          <div className="text-center py-20">
            <CheckCircle className="h-16 w-16 text-green-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-500">All caught up!</h3>
            <p className="text-gray-400 mt-1">No pending registrations to review.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {staff.map((s) => (
              <div key={s.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1 space-y-2 cursor-pointer" onClick={() => navigate('/admin/staff/' + s.id + '/review')}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-800">{s.firstName} {s.lastName}</h3>
                        <p className="text-xs text-gray-400">Profile ID: {s.id}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{s.qualification} · {s.experienceYears} yrs</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{s.city}, {s.state}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{s.gender}</span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => handleApprove(s.id)} disabled={actionLoading === s.id}
                      className="flex items-center gap-1.5 bg-green-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-green-700 transition disabled:opacity-50">
                      {actionLoading === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />} Approve
                    </button>
                    <button onClick={() => setRejectId(s.id)} disabled={actionLoading === s.id}
                      className="flex items-center gap-1.5 bg-red-50 text-red-600 border border-red-200 px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-red-100 transition disabled:opacity-50">
                      <XCircle className="h-4 w-4" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {rejectId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
              <h3 className="font-bold text-slate-800 mb-4">Reject Registration</h3>
              <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={3} className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none" />
              <div className="flex gap-3 mt-4">
                <button onClick={() => { setRejectId(null); setRejectReason(''); }}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl font-semibold text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                <button onClick={handleReject} disabled={actionLoading === rejectId}
                  className="flex-1 py-2.5 bg-red-600 text-white rounded-xl font-bold text-sm hover:bg-red-700 disabled:opacity-50">
                  {actionLoading === rejectId ? 'Rejecting...' : 'Confirm Reject'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}